/**
 * Tutorial Walkthrough Component
 * Step-by-step tooltip walkthrough that highlights key UI elements
 */
import { useState, useEffect, useCallback, useRef } from 'react';

interface TutorialWalkthroughProps {
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface Step {
  target: string;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    target: 'add-job',
    title: 'Add a Job',
    description: 'Start by adding a job from a URL or manually.',
  },
  {
    target: 'job-card',
    title: 'Job Cards',
    description: 'Each job card shows the position details and status.',
  },
  {
    target: 'generate-resume',
    title: 'Generate Resume',
    description: 'Click to generate all resume sections using AI.',
  },
  {
    target: 'resume-sections',
    title: 'Resume Sections',
    description: 'Click any section to view, edit, or change its generation type.',
  },
  {
    target: 'files-list',
    title: 'Your Files',
    description: 'Access your generated resume and cover letter files here.',
  },
];

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

type TooltipPosition = 'above' | 'below';

export function TutorialWalkthrough({ isActive, onComplete, onSkip }: TutorialWalkthroughProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>('below');
  const tooltipRef = useRef<HTMLDivElement>(null);

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const updateTargetRect = useCallback(() => {
    if (!isActive || !step) return;

    const element = document.querySelector(`[data-tutorial="${step.target}"]`);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right,
      });

      // Position tooltip above if target is in the lower half of the viewport
      const viewportMidpoint = window.innerHeight / 2;
      setTooltipPosition(rect.top > viewportMidpoint ? 'above' : 'below');
    } else {
      setTargetRect(null);
    }
  }, [isActive, step]);

  // Update position on mount and when step changes
  useEffect(() => {
    updateTargetRect();
  }, [updateTargetRect, currentStep]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isActive) return;

    const handleUpdate = () => updateTargetRect();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isActive, updateTargetRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLastStep) {
          onComplete();
        } else {
          setCurrentStep((prev) => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        setCurrentStep((prev) => prev - 1);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, isLastStep, currentStep, onComplete, onSkip]);

  // Reset step when walkthrough becomes active
  useEffect(() => {
    if (isActive) {
      setCurrentStep(0);
    }
  }, [isActive]);

  if (!isActive) return null;

  const padding = 8;

  function handleNext() {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }

  // Calculate tooltip positioning
  const tooltipStyle: React.CSSProperties = {};
  const arrowStyle: React.CSSProperties = {};

  if (targetRect) {
    const tooltipOffset = 16;
    tooltipStyle.position = 'fixed';
    tooltipStyle.zIndex = 60;
    tooltipStyle.left = Math.max(
      16,
      Math.min(
        targetRect.left + targetRect.width / 2 - 160,
        window.innerWidth - 336
      )
    );

    if (tooltipPosition === 'above') {
      tooltipStyle.bottom = window.innerHeight - targetRect.top + padding + tooltipOffset;
    } else {
      tooltipStyle.top = targetRect.bottom + padding + tooltipOffset;
    }

    // Arrow horizontal position relative to tooltip
    const tooltipLeft = tooltipStyle.left as number;
    const targetCenter = targetRect.left + targetRect.width / 2;
    arrowStyle.left = Math.max(24, Math.min(targetCenter - tooltipLeft, 296));
  }

  return (
    <div className="fixed inset-0 z-50">
      {/* SVG overlay with cutout */}
      <svg
        className="fixed inset-0 w-full h-full"
        style={{ zIndex: 50 }}
        onClick={onSkip}
      >
        <defs>
          <mask id="tutorial-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - padding}
                y={targetRect.top - padding}
                width={targetRect.width + padding * 2}
                height={targetRect.height + padding * 2}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#tutorial-spotlight-mask)"
        />
      </svg>

      {/* Spotlight border around target */}
      {targetRect && (
        <div
          className="fixed rounded-lg ring-2 ring-blue-400 ring-offset-2 pointer-events-none"
          style={{
            zIndex: 55,
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed w-80 bg-white rounded-xl shadow-2xl border border-gray-200"
        style={tooltipStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Arrow */}
        {targetRect && (
          <div
            className={`absolute w-3 h-3 bg-white border-gray-200 transform rotate-45 ${
              tooltipPosition === 'above'
                ? 'bottom-[-7px] border-r border-b'
                : 'top-[-7px] border-l border-t'
            }`}
            style={{
              left: arrowStyle.left,
              marginLeft: -6,
            }}
          />
        )}

        <div className="p-5">
          {/* Step counter */}
          <div className="text-xs font-medium text-blue-500 uppercase tracking-wide mb-2">
            Step {currentStep + 1} of {steps.length}
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            {step.description}
          </p>

          {/* No target found message */}
          {!targetRect && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2 mb-4">
              This element is not visible on the current page. Click Next to continue.
            </p>
          )}

          {/* Progress bar */}
          <div className="w-full h-1 bg-gray-100 rounded-full mb-4">
            <div
              className="h-1 bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors px-2 py-1"
                >
                  Back
                </button>
              )}
              <button
                onClick={onSkip}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors px-2 py-1"
              >
                Skip
              </button>
            </div>
            <button
              onClick={handleNext}
              className="px-5 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
